import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditPromocodeBatchMutation } from "@modules/promocodeBatches/services/mutations";
import { PromocodeBatchFormValues, promocodeBatchSchema } from "@modules/promocodeBatches/validations";
import { listPromocodeBatchesQueryKey } from "@modules/promocodeBatches/services/queries";
import { EditPromocodeBatchModalProps } from "./types";

export function useEditPromocodeModal({ modalRef, onComplete }: EditPromocodeBatchModalProps) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<PromocodeBatchFormValues>({
    resolver: zodResolver(promocodeBatchSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditPromocodeBatchMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listPromocodeBatchesQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("promocode_batch_admin.success.edit"),
      });

      onComplete();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: PromocodeBatchFormValues) => {
    const promocodeId = modalRef.current?.getId();
    await mutateAsync({ id: promocodeId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
